import React, { useState, useEffect } from "react";
import { FaPlay, FaPause } from "react-icons/fa";
import { MdOutlineRestartAlt } from "react-icons/md";
import Popup from "../Popup";
import { auth } from "../../firebase";
import { updateDocument, getDocument } from "../../firestore";
import styles from "../games.module.css";

const words = [
  "screenreader", "a11y", "alttext", "captioning", "wcag",  
  "aria", "contrast", "keyboardnav", "voiceover", "braille",  
  "assistivetech", "inclusive", "readability", "legibility", "semantic",  
  "descriptive", "transcripts", "usability", "autocaptions", "altattributes"
];


const scrambleWord = (word) => {
  let scrambled = word;
  while (scrambled === word) {
    scrambled = word
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");
  }
  return scrambled;
};

const WordScramble = () => {
  const [currentWord, setCurrentWord] = useState("");
  const [scrambledWord, setScrambledWord] = useState("");
  const [userGuess, setUserGuess] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [score, setScore] = useState(100);
  const [lives, setLives] = useState(3);
  const [showPopup, setShowPopup] = useState(false);
  const [popupCooldown, setPopupCooldown] = useState(false);
  const [showGameOverPopup, setShowGameOverPopup] = useState(false);
  const rescramblePenalty = 5;
  const [startTime, setStartTime] = useState(0);
  const uid = auth.currentUser ? auth.currentUser.uid : null;

  useEffect(() => {
    if (lives <= 0) {
      gameOverDBUpdate();
      setShowGameOverPopup(true);
    } else {
      setNewWord();
    }
  }, [lives]);

  const handleExitGame = () => {
    window.location.href = "/home";
  };

  const exitGame = () => {
    window.location.href = "/games";
  };

  async function checkGameStarted() {
    const queryParams = new URLSearchParams(window.location.search);
    const gameId = queryParams.get("id");
    if (!gameId) return;

    const userId = auth.currentUser.uid;
    const subCollectionPath = `gameplay/${userId}/gameplays/`;

    try {
      const gameDoc = await getDocument(subCollectionPath, gameId);
      if (gameDoc?.gameStarted) {
        const redirectUrl = `${window.location.origin}/instructions/2048`;
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error("Error checking game status:", error);
    }
  }

  async function gameOverDBUpdate() {
    const queryParams = new URLSearchParams(window.location.search);
    var gameId = queryParams.get("id");
    const userId = auth.currentUser.uid;
    const subCollectionPath = `gameplay/${userId}/gameplays/`;
    let currentScore = score;
    const gameplayData = {
      lives: lives,
      score: currentScore,
    };
    await updateDocument(subCollectionPath, gameId, gameplayData);
    await totalGameDataUpdate(currentScore);
  }

  async function totalGameDataUpdate(score) {
    const subCollectionPath = `gameplay`;
    const userId = auth.currentUser.uid;
    var docData = await getDocument(subCollectionPath, userId);
    let oldScore = docData.score;
    let total_games = docData.games_played + 1;
    let currentScore = score + oldScore;
    const gameplayData = {
      games_played: total_games,
      score: currentScore,
    };
    await updateDocument(subCollectionPath, userId, gameplayData);
  }

  const setNewWord = () => {
    const randomWord = words[Math.floor(Math.random() * words.length)];
    setCurrentWord(randomWord);
    setScrambledWord(scrambleWord(randomWord));
    setUserGuess("");
    setAttempts(0);
    setStartTime(Date.now());
  };

  const handleInputChange = (e) => {
    setUserGuess(e.target.value);
  };

  const handleSubmit = () => {
    setAttempts(attempts + 1);

    if (userGuess.toLowerCase() === currentWord.toLowerCase()) {
      const elapsedTime = (Date.now() - startTime) / 1000;
      const timeBonus = Math.max(50 - elapsedTime, 0);

      setScore((prevScore) => Math.max(prevScore + Math.floor(timeBonus), 0));
      setNewWord();
    } else {
      alert("Try again!");
    }
  };

  const rescrambleWord = () => {
    setScrambledWord(scrambleWord(currentWord));
    setScore((prevScore) => Math.max(prevScore - rescramblePenalty, 0));
  };

  const handlePopupClose = () => {
    setShowPopup(false);
    setTimeout(() => setPopupCooldown(false), 15000);
  };

  const handleGameOverRestart = () => {
    setScore(100);
    setLives(3);
    setShowGameOverPopup(false);
    setNewWord();
  };

  useEffect(() => {
    const popupInterval = setInterval(() => {
      if (!popupCooldown) {
        setShowPopup(true);
        setPopupCooldown(true);
      }
    }, 15000);

    return () => clearInterval(popupInterval);
  }, [popupCooldown]);

  const renderHearts = () => {
    return Array.from({ length: lives }).map((_, index) => (
      <span
        key={index}
        className={`text-red-500 text-2xl ${
          index < lives ? "opacity-100" : "opacity-30"
        }`}
      >
        ❤️
      </span>
    ));
  };

  return (
    <div className="min-h-screen bg-[var(--background-color)] py-8 px-4 sm:px-6 lg:px-8 animate-fadeIn">
      <div className="max-w-7xl mx-auto">
        {/* Page Title with Gradient */}
        <h1 className="font-['Jersey_25',sans-serif] text-4xl md:text-5xl font-bold text-center mb-10 text-yellow-500 bg-gradient-to-r from-[var(--primary-color)] via-[var(--interactive-color)] to-[var(--accent-color)] text-transparent bg-clip-text transform transition-all">
          Word Scramble
        </h1>

        {/* Game Stats and Controls */}
        <div className="bg-[var(--surface-color)] rounded-2xl shadow-lg overflow-hidden border border-[var(--border-color)]">
          <div className="bg-[var(--primary-color)] py-4 px-6">
            <div className="flex items-center">
              <div className="text-white text-3xl w-full flex justify-between items-center p-4">
                <div className="flex items-center gap-6">
                  <h1 className="text-2xl font-bold text-[var(--text-color)]">
                    Score: {score}
                  </h1>
                  <h1 className="text-2xl font-bold text-[var(--text-color)]">
                    Lives: {renderHearts()}
                  </h1>
                  <h1 className="text-2xl font-bold text-[var(--text-color)]">
                    Attempts: {attempts}
                  </h1>
                </div>

                <div className="flex items-center gap-4">
                  <button
                    className="group relative inline-flex items-center px-6 py-3 bg-[var(--interactive-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--interactive-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
                    onClick={handleGameOverRestart}
                  >
                    <MdOutlineRestartAlt className="mr-2 text-xl" />
                    Restart
                  </button>

                  <button
                    className="group relative inline-flex items-center px-6 py-3 bg-[var(--error-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--error-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
                    onClick={exitGame}
                  >
                    Exit
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Game Container */}
          <div className="w-full max-w-4xl h-[600px] relative border-4 border-[var(--border-color)] rounded-lg bg-[var(--surface-color)] overflow-hidden mx-auto p-8">
            <h2 className="text-2xl font-bold text-[var(--text-color)] mb-4">
              Scrambled Word: {scrambledWord}
            </h2>
            <input
              className="w-full p-2 mb-4 border border-[var(--border-color)] rounded-lg text-black"
              type="text"
              value={userGuess}
              onChange={handleInputChange}
              placeholder="Your guess"
              disabled={showPopup || showGameOverPopup}
            />
            <div className="flex gap-4">
              <button
                className="group relative inline-flex items-center px-6 py-3 bg-[var(--interactive-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--interactive-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
                onClick={rescrambleWord}
                disabled={showPopup || showGameOverPopup}
              >
                Rescramble Word (Lose {rescramblePenalty} points)
              </button>
              <button
                className="group relative inline-flex items-center px-6 py-3 bg-[var(--interactive-color)] text-white rounded-lg text-xl font-bold transform transition-all duration-300 hover:bg-opacity-90 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[var(--interactive-color)] focus:ring-offset-2 focus:ring-offset-[var(--background-color)]"
                onClick={handleSubmit}
                disabled={showPopup || showGameOverPopup}
              >
                Submit Guess
              </button>
            </div>
          </div>
        </div>

        {/* Popup and Game Over Screens */}
        {showPopup && (
          <Popup
            onClose={handlePopupClose}
            score={score}
            setScore={setScore}
            lives={lives}
            setLives={setLives}
            useLives={true}
          />
        )}

        {showGameOverPopup && (
          <>
            <div className="overlay"></div> {/* Dark overlay */}
            <div className="game-over-popup">
              <h2>Game Over!</h2>
              <p>Your final score: {score}</p>
              <button
                className="word-restart-btn"
                onClick={handleGameOverRestart}
              >
                Restart Game
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WordScramble;